import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Loader2, Check } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PlanMyTripModal({ open, onClose, initial }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    destination: "",
    travel_date: "",
    travelers: "",
    budget: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (initial?.destination) {
      setForm((f) => ({ ...f, destination: initial.destination }));
    }
  }, [initial]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open && !done) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
    } catch (e2) {
      setErr("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setDone(false);
      setForm({ name: "", email: "", phone: "", destination: "", travel_date: "", travelers: "", budget: "", message: "" });
    }, 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0A2342]/70 backdrop-blur-md"
          onClick={close}
          data-testid="plan-trip-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl"
          >
            <button
              onClick={close}
              data-testid="plan-trip-close"
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#F8F9FB] hover:bg-gray-200 flex items-center justify-center z-10"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-[#0A2342]" />
            </button>

            {done ? (
              <div className="p-12 text-center" data-testid="plan-trip-success">
                <div className="w-16 h-16 rounded-full bg-[#C9A227]/15 flex items-center justify-center mx-auto">
                  <Check className="w-7 h-7 text-[#C9A227]" />
                </div>
                <h3 className="font-display text-3xl text-[#0A2342] mt-6">Thank you!</h3>
                <p className="text-[#6B7280] mt-3 leading-relaxed max-w-md mx-auto">
                  Your dream journey is now in our hands. A senior consultant will reach out within 24 hours with a personalised proposal.
                </p>
                <button onClick={close} className="btn-navy mt-8" data-testid="plan-trip-close-success">Continue exploring</button>
              </div>
            ) : (
              <div className="p-8 md:p-10">
                <div className="overline">Plan My Trip</div>
                <h3 className="font-display text-3xl md:text-4xl text-[#0A2342] mt-2 leading-[1.1]">
                  Tell us about your dream journey.
                </h3>
                <p className="text-[#6B7280] mt-2 text-sm">No spam, no obligation. A consultant responds within 24 hours.</p>

                <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="Full name" value={form.name} onChange={set("name")} required testid="form-name" />
                  <Input label="Email" type="email" value={form.email} onChange={set("email")} required testid="form-email" />
                  <Input label="Phone" value={form.phone} onChange={set("phone")} testid="form-phone" />
                  <Input label="Destination" value={form.destination} onChange={set("destination")} placeholder="Maldives, Bali…" testid="form-destination" />
                  <Input label="Travel date" type="date" value={form.travel_date} onChange={set("travel_date")} testid="form-date" />
                  <Input label="Travellers" value={form.travelers} onChange={set("travelers")} placeholder="2 adults" testid="form-travelers" />
                  <div className="md:col-span-2">
                    <Select label="Approx. budget per person" value={form.budget} onChange={set("budget")} testid="form-budget">
                      <option value="">Select range…</option>
                      <option>Under ₹75,000</option>
                      <option>₹75,000 – ₹1.5L</option>
                      <option>₹1.5L – ₹3L</option>
                      <option>₹3L – ₹5L</option>
                      <option>₹5L+ Ultra Luxury</option>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs uppercase tracking-[0.18em] text-[#6B7280] mb-2">Anything else?</label>
                    <textarea
                      value={form.message}
                      onChange={set("message")}
                      rows={3}
                      data-testid="form-message"
                      placeholder="Special occasions, dietary needs, must-sees…"
                      className="w-full bg-[#F8F9FB] border border-gray-200 rounded-2xl px-4 py-3 text-[#1F2937] placeholder-gray-400 focus:bg-white"
                    />
                  </div>

                  {err && <div className="md:col-span-2 text-sm text-red-500" data-testid="form-error">{err}</div>}

                  <button type="submit" disabled={loading} data-testid="form-submit" className="btn-gold md:col-span-2 justify-center">
                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Submit Enquiry <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </form>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Input({ label, type = "text", value, onChange, required, placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#6B7280] mb-2">{label}{required && " *"}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-[#F8F9FB] border border-gray-200 rounded-full px-5 py-3 text-[#1F2937] placeholder-gray-400 focus:bg-white"
      />
    </div>
  );
}

function Select({ label, value, onChange, children, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#6B7280] mb-2">{label}</label>
      <select
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-[#F8F9FB] border border-gray-200 rounded-full px-5 py-3 text-[#1F2937] focus:bg-white"
      >
        {children}
      </select>
    </div>
  );
}
